import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('SeoApprovalService', 'users');

/**
 * Client for the LO profile SEO indexing approval gate.
 *
 * The approval data lives in a private-schema table with no client access;
 * everything goes through two admin-gated RPCs whose payload shapes differ:
 * `list_lo_profile_seo_approvals` returns snake_case rows, while
 * `set_lo_profile_seo_approval` returns a camelCase JSON object. Both are
 * normalized here into one validated frontend type. The database keeps only
 * the LATEST decision per officer — there is no history ledger.
 */

export type SeoApprovalStatus = 'never_reviewed' | 'approved' | 'not_approved';

export interface SeoApprovalState {
  status: SeoApprovalStatus;
  approved: boolean;
  note: string | null;
  /** Approver user id (uuid). Resolve to a name before display — never show the raw uuid. */
  approvedBy: string | null;
  /**
   * Timestamp of the last decision. Sourced from updated_at — approved_at is
   * NULL whenever approval is revoked, so it cannot represent "last decided".
   */
  decidedAt: string | null;
}

export type SeoApprovalFailure = 'forbidden' | 'malformed' | 'error';

export type SeoApprovalResult =
  | { ok: true; state: SeoApprovalState }
  | { ok: false; reason: SeoApprovalFailure; message: string };

const NEVER_REVIEWED: SeoApprovalState = {
  status: 'never_reviewed',
  approved: false,
  note: null,
  approvedBy: null,
  decidedAt: null
};

// Fail-closed field validation: every field must be EXPLICITLY present.
// The RPCs always emit complete shapes (RETURNS TABLE columns; a
// jsonb_build_object that never strips nulls), so an absent key means the
// payload is not what this client was written against.
const isExplicitNullableString = (value: unknown): value is string | null =>
  value === null || typeof value === 'string';

const isValidTimestamp = (value: unknown): value is string =>
  typeof value === 'string' && Number.isFinite(Date.parse(value));

const isExplicitNullableTimestamp = (value: unknown): value is string | null =>
  value === null || isValidTimestamp(value);

const asState = (
  approved: unknown,
  note: unknown,
  approvedBy: unknown,
  decidedAt: unknown
): SeoApprovalState | null => {
  if (typeof approved !== 'boolean') {
    return null;
  }
  if (!isExplicitNullableString(note) || !isExplicitNullableString(approvedBy)) {
    return null;
  }
  // updated_at is NOT NULL in the schema; an absent or unparseable decision
  // timestamp is a malformed payload, never an "Invalid Date" in the UI.
  if (!isValidTimestamp(decidedAt)) {
    return null;
  }
  return {
    status: approved ? 'approved' : 'not_approved',
    approved,
    note,
    approvedBy,
    decidedAt
  };
};

/**
 * Normalize a snake_case row from list_lo_profile_seo_approvals. The FULL
 * RETURNS TABLE shape is validated — including fields this client does not
 * carry into state (organization_id NOT NULL, approved_at nullable
 * timestamp) — so a contract drift fails closed instead of half-parsing.
 */
const fromListRow = (row: Record<string, unknown>): SeoApprovalState | null => {
  if (typeof row.organization_id !== 'string' || !isExplicitNullableTimestamp(row.approved_at)) {
    return null;
  }
  return asState(row.approved, row.note, row.approved_by, row.updated_at);
};

/**
 * Normalize the camelCase payload from set_lo_profile_seo_approval, again
 * validating the complete emitted shape including the uncarried fields.
 */
const fromMutationPayload = (payload: Record<string, unknown>): SeoApprovalState | null => {
  if (
    typeof payload.organizationId !== 'string' ||
    !isExplicitNullableTimestamp(payload.approvedAt)
  ) {
    return null;
  }
  return asState(payload.approved, payload.note, payload.approvedBy, payload.updatedAt);
};

const isPermissionDenied = (error: { code?: string | null } | null): boolean =>
  error?.code === '42501';

const normalizeRoles = (roles: unknown): string[] =>
  Array.isArray(roles) ? roles.filter((role): role is string => typeof role === 'string') : [];

/**
 * The database contract for who may operate the approval gate: users.roles
 * containing admin OR super_admin. This single predicate is shared by the
 * Manage Users profile route (UnifiedUserProfile) and this service so the
 * UI can never diverge from what the RPCs enforce.
 */
export const hasProfileAdminRole = (roles: unknown): boolean => {
  const normalized = normalizeRoles(roles);
  return normalized.includes('admin') || normalized.includes('super_admin');
};

/**
 * Whether the CURRENT session user may operate the approval controls, using
 * the same database contract as the RPCs: users.roles containing admin OR
 * super_admin. Callers must not invoke the approval RPCs when this is false.
 */
export async function fetchActorCanManageSeoApproval(): Promise<boolean> {
  const { data: auth } = await supabase.auth.getUser();
  const actorId = auth?.user?.id;
  if (!actorId) {
return false;
}

  // Mirror the database contract in full: the RPC's admin check also
  // requires an active, non-deleted user. An ineligible actor simply never
  // sees the controls (the RPC would reject them regardless).
  const { data, error } = await supabase
    .from('users')
    .select('roles')
    .eq('id', actorId)
    .is('deleted_at', null)
    .eq('employment_status', 'active')
    .single();

  if (error || !data) {
    return false;
  }

  return hasProfileAdminRole(data.roles);
}

/** Best-effort approver display name. Falls back to null — never a uuid. */
export async function fetchApproverDisplayName(userId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('users')
    .select('first_name, last_name')
    .eq('id', userId)
    .maybeSingle();

  if (error || !data) {
return null;
}
  const name = [data.first_name, data.last_name]
    .filter((part): part is string => typeof part === 'string' && part.trim() !== '')
    .join(' ')
    .trim();
  return name === '' ? null : name;
}

/**
 * Current approval state for one officer. Admin-only (the RPC enforces it);
 * an absent row is an explicit "never reviewed", not an error.
 */
export async function getSeoApproval(targetUserId: string): Promise<SeoApprovalResult> {
  const { data, error } = await supabase.rpc('list_lo_profile_seo_approvals');

  if (error) {
    const reason: SeoApprovalFailure = isPermissionDenied(error) ? 'forbidden' : 'error';
    log.error('Failed to list SEO approvals', { metadata: { targetUserId, code: error.code } }, error);
    return { ok: false, reason, message: error.message };
  }

  if (!Array.isArray(data)) {
    return { ok: false, reason: 'malformed', message: 'Unexpected list payload shape' };
  }

  const row = data.find(
    (candidate): candidate is Record<string, unknown> =>
      typeof candidate === 'object' && candidate !== null &&
      (candidate as Record<string, unknown>).user_id === targetUserId
  );

  if (!row) {
    return { ok: true, state: NEVER_REVIEWED };
  }

  const state = fromListRow(row);
  if (!state) {
    return { ok: false, reason: 'malformed', message: 'Approval row failed validation' };
  }
  return { ok: true, state };
}

/** Record an approval decision. Admin-only; the RPC enforces org scoping. */
export async function setSeoApproval(
  targetUserId: string,
  approved: boolean,
  note: string | null
): Promise<SeoApprovalResult> {
  const { data, error } = await supabase.rpc('set_lo_profile_seo_approval', {
    p_user_id: targetUserId,
    p_approved: approved,
    p_note: note
  });

  if (error) {
    const reason: SeoApprovalFailure = isPermissionDenied(error) ? 'forbidden' : 'error';
    log.error('Failed to set SEO approval', { metadata: { targetUserId, approved, code: error.code } }, error);
    return { ok: false, reason, message: error.message };
  }

  if (typeof data !== 'object' || data === null) {
    return { ok: false, reason: 'malformed', message: 'Unexpected mutation payload shape' };
  }

  const payload = data as Record<string, unknown>;
  // The decision must be about the officer we asked about — a mismatched
  // echo is treated as malformed, never applied to the UI.
  if (payload.userId !== targetUserId) {
    return { ok: false, reason: 'malformed', message: 'Mutation payload targets a different user' };
  }

  const state = fromMutationPayload(payload);
  if (!state) {
    return { ok: false, reason: 'malformed', message: 'Mutation payload failed validation' };
  }
  return { ok: true, state };
}
